import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Rows that slide to their new place instead of teleporting to it.
 *
 * A support queue is sorted by most recent activity, so every message a customer
 * sends moves their conversation to the top — and the row an agent was reading a
 * moment ago is suddenly somewhere else with nothing to connect the two positions.
 * The list is *correct* either way; the complaint is that the agent has to re-find
 * their place after every arrival, which on a busy afternoon is constant.
 *
 * ## FLIP, and why it is the only approach that works here
 *
 * The order comes from the server. React re-renders the list in the new order and
 * the browser paints it there, all before anything can be animated — the old
 * positions are gone by the time an effect runs. FLIP works backwards from that:
 * record where each row *was*, let the new layout happen, then transform each row
 * back to its old position and release it. The browser animates a transform on the
 * compositor, so a queue of a hundred rows reordering costs no layout work at all.
 *
 * The alternative — animating height, or reordering with a library that owns the
 * list — would mean the queue could no longer simply render what the server sent.
 *
 * ## What it deliberately does not animate
 *
 * The first render, where there is no previous position and a list sliding in from
 * nowhere is a distraction. Rows that did not move. And anything past
 * `MAX_TRAVEL_PX`, which is not a movement but a scroll, a filter change or a
 * reload — animating those produces a row flying across the screen, which is the
 * opposite of the calm this exists to provide.
 */

/**
 * Beyond this, the row did not move — the list did.
 *
 * A conversation rising past a screenful is a filter change or a fresh load, not a
 * message arriving.
 */
const MAX_TRAVEL_PX = 900;

/** Long enough to follow with the eye, short enough not to be in the way. */
const DURATION_MS = 260;

/** Decelerating: fast off the mark, settling gently. */
const EASING = "cubic-bezier(0.2, 0.8, 0.2, 1)";

export interface ReorderAnimation {
  /**
   * Attach to each row, keyed by whatever identifies it across renders.
   *
   * A callback ref rather than a map the caller maintains: React calls it with
   * `null` when a row unmounts, which is the only reliable moment to stop tracking
   * a conversation that has been deleted or filtered away.
   *
   * **The returned function is cached per key, and has to be.** React compares refs
   * by identity: a fresh closure each render is a *different* ref, so React detaches
   * the old one — calling it with `null` — and attaches the new one, on every single
   * render, before any effect runs. The detach would clear the row's recorded
   * position, every row would look new, and nothing would ever animate. The bug
   * would present as the feature silently not working.
   */
  readonly rowRef: (key: string) => (node: HTMLElement | null) => void;
}

/**
 * @param order The keys in the order they are currently rendered. The effect runs
 *   when this changes, which is what ties the animation to a reorder rather than to
 *   every unrelated re-render.
 */
export function useReorderAnimation(order: readonly string[]): ReorderAnimation {
  const nodes = useRef(new Map<string, HTMLElement>());
  const previousTops = useRef(new Map<string, number>());
  const callbacks = useRef(new Map<string, (node: HTMLElement | null) => void>());

  const rowRef = useCallback((key: string) => {
    const existing = callbacks.current.get(key);

    if (existing !== undefined) return existing;

    const callback = (node: HTMLElement | null): void => {
      if (node === null) {
        nodes.current.delete(key);
        previousTops.current.delete(key);
        callbacks.current.delete(key);
        return;
      }

      nodes.current.set(key, node);
    };

    callbacks.current.set(key, callback);
    return callback;
  }, []);

  useLayoutEffect(() => {
    /*
      Honoured rather than treated as a preference. Motion sickness and vestibular
      disorders are real, and this animation exists for comfort — inflicting it on
      somebody who has asked their operating system for stillness would be a
      straightforward own goal.
    */
    const stillness = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const before = previousTops.current;
    const after = new Map<string, number>();

    for (const [key, node] of nodes.current) {
      const top = node.getBoundingClientRect().top;
      after.set(key, top);

      if (stillness) continue;

      const was = before.get(key);

      // Absent means the row is new — see the note above about the first render.
      if (was === undefined) continue;

      const travelled = was - top;

      if (travelled === 0 || Math.abs(travelled) > MAX_TRAVEL_PX) continue;

      // Put it back where it was, with no transition, so nothing is drawn in the
      // new position first.
      node.style.transition = "none";
      node.style.transform = `translateY(${String(travelled)}px)`;

      // Then let it go, on the next frame — setting both in one frame is a single
      // style computation and the browser has nothing to animate between.
      requestAnimationFrame(() => {
        node.style.transition = `transform ${String(DURATION_MS)}ms ${EASING}`;
        node.style.transform = "";
      });
    }

    previousTops.current = after;
  }, [order]);

  return { rowRef };
}
